import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid2,
  Container,
  Typography,
  Paper,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import handleAxiosError from "../../components/ErrorHandler";
import { notifErr, notifOk } from "../../components/Functions";
import { Loading } from "../../components/Loading";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { TrainingArea } from "../../model/entity/TrainingArea";
import {
  trainingAreaValidationObjInit,
  validateTrainingArea,
} from "../../model/validation/TrainingAreaValidation";
import {
  deleteFetcher,
  getFetcher,
  putFetcher,
} from "../../components/Fetchers";

const EditSchedule = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [trainingArea, setTrainingArea] = useState<TrainingArea | null>(null);
  const [trainingAreaErr, setTrainingAreaErr] = useState(
    trainingAreaValidationObjInit
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getFetcher(
          `${process.env.REACT_APP_API_URL}/training-areas/${id}`
        );
        setTrainingArea(response.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    if (!trainingArea) return;
    if (triedSubmit) setTrainingAreaErr(validateTrainingArea(trainingArea));
  }, [trainingArea, triedSubmit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!trainingArea) return;
    setTrainingArea({ ...trainingArea, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trainingArea) return;

    setTriedSubmit(true);

    const areaErrors = validateTrainingArea(trainingArea);
    setTrainingAreaErr(areaErrors);

    if (Object.values(areaErrors).some((val) => val === false)) return;

    try {
      await putFetcher(
        `${process.env.REACT_APP_API_URL}/training-areas/${id}`,
        trainingArea
      );
      notifOk("Změny byly uloženy.");
      navigate(-1);
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleDeleteConfirm = async () => {
    if (!trainingArea) return;
    try {
      await deleteFetcher(
        `${process.env.REACT_APP_API_URL}/training-areas/${trainingArea.id}`
      );
      notifOk("Sportoviště úspěšně odstraněno.");
      navigate(-1);
    } catch (err) {
      notifErr(handleAxiosError(err));
    } finally {
      setOpenDialog(false);
    }
  };

  if (trainingArea === null) return <Loading />;

  const dialogMsg = (
    <div>
      Opravdu chcete trvale smazat sportoviště
      <b>
        {" "}
        {trainingArea.name} ({trainingArea.code})
      </b>
      ?<br />
      Tato akce je nevratná.
    </div>
  );

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pb: "2rem",
      }}
    >
      <ConfirmationDialog
        open={openDialog}
        setOpen={setOpenDialog}
        msg={dialogMsg}
        handleSubmit={handleDeleteConfirm}
      />
      <Paper elevation={3} sx={{ padding: "1.5rem" }}>
        <Typography variant="h5" sx={{ marginBottom: "1.5rem" }}>
          {trainingArea.name}
        </Typography>
        <Grid2 container spacing={3} size={{ xs: 12, md: 6 }}>
          <Grid2 size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Název"
              required
              name="name"
              value={trainingArea.name}
              error={!trainingAreaErr.name}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Zkratkový kód"
              required
              name="code"
              value={trainingArea.code}
              error={!trainingAreaErr.code}
              onChange={handleInputChange}
            />
          </Grid2>

          <Grid2
            container
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
            id="top"
          >
            <Grid2 id="middle" size={{ xs: 12, md: 6 }}>
              <Button
                className="deleteBtn"
                variant="contained"
                onClick={() => setOpenDialog(true)}
              >
                Smazat
              </Button>
            </Grid2>
            <Grid2
              id="bottomxd"
              size={{ xs: 12, md: 6 }}
              sx={{ display: "flex", justifyContent: "right", gap: 2 }}
            >
              <Button
                className="cancelBtn"
                variant="outlined"
                onClick={handleCancel}
              >
                Zrušit
              </Button>
              <Button
                className="submitBtn"
                variant="outlined"
                onClick={handleSubmit}
              >
                Uložit
              </Button>
            </Grid2>
          </Grid2>
        </Grid2>
      </Paper>
    </Container>
  );
};

export default EditSchedule;
